/**
 * THE GO-LIVE CHECKLIST, WORKING.
 *
 * The runbook is still the prose, one tab across. This is the same steps as
 * state: what is done, what is not, and for the ones a person has to judge,
 * who said so and when.
 *
 * TWO HALVES, AND THE DIFFERENCE IS WHY THIS IS WORTH HAVING.
 *
 *   Read from the record  Computed on every load. No checkbox, because a tick
 *                         there would be a way to disagree with the database.
 *   Checked by us         A gcloud command somebody ran, a report somebody
 *                         read, a call that happened. The tick carries a name
 *                         and a time.
 *
 * The platform section is about the installation and is answered once. Choose
 * a customer and the three sections below it fill in for that customer.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { call, isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { OperationsTabs } from './OperationsTabs';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Console.module.css';

interface StepState {
  key: string;
  section: 'platform' | 'creating' | 'onboarding' | 'firstMonth';
  label: string;
  detail: string;
  source: 'observed' | 'tick';
  command?: string;
  preflight?: boolean;
  done: boolean;
  byEmail: string | null;
  at: string | null;
  note: string | null;
  evidence: string | null;
}

interface Checklist {
  orgId: string | null;
  orgName: string | null;
  steps: StepState[];
  platformDone: number; platformTotal: number;
  customerDone: number; customerTotal: number;
}

const SECTIONS: { id: StepState['section']; title: string; blurb: string }[] = [
  { id: 'platform', title: 'Before you create anything', blurb: 'The installation, not the customer. True for everybody once it is true.' },
  { id: 'creating', title: 'Creating the customer', blurb: 'The first hour: the organization exists, somebody owns it, and the money is agreed.' },
  { id: 'onboarding', title: 'Customer onboarding', blurb: 'Their library is in, and the product has been made to work once by us.' },
  { id: 'firstMonth', title: 'The first month', blurb: 'Weekly, for four weeks. These recur: tick them again each week.' },
];

const on = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

export function GoLivePage() {
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org') ?? '';
  const queryClient = useQueryClient();
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');

  /*
    `orgId`, not `id`. Getting that wrong is not a typo you notice: an
    <option> with no value attribute falls back to its own TEXT, so the picker
    silently sent the organization's NAME, the lookup asked for orgs/OrgOne
    instead of orgs/orgone, and the page said "Something went wrong".
  */
  const orgs = useQuery({
    queryKey: ['console-orgs'],
    queryFn: () => call<Record<string, never>, { organizations: { orgId: string; name: string }[] }>('sysAdminListOrganizations', {}),
  });

  const checklist = useQuery({
    queryKey: ['go-live', orgId],
    queryFn: () => call<{ orgId?: string }, Checklist>('sysAdminGetGoLiveChecklist', orgId ? { orgId } : {}),
  });

  const setStep = useMutation({
    mutationFn: (input: { key: string; done: boolean; note?: string; platform: boolean }) =>
      call<{ orgId?: string; key: string; done: boolean; note?: string }, unknown>('sysAdminSetGoLiveItem', {
        ...(input.platform ? {} : { orgId }),
        key: input.key, done: input.done, ...(input.note ? { note: input.note } : {}),
      }),
    onSuccess: () => {
      setNoteFor(null); setNoteText('');
      void queryClient.invalidateQueries({ queryKey: ['go-live'] });
    },
  });

  const bySection = useMemo(() => {
    const map = new Map<string, StepState[]>();
    for (const s of checklist.data?.steps ?? []) {
      map.set(s.section, [...(map.get(s.section) ?? []), s]);
    }
    return map;
  }, [checklist.data]);

  if (checklist.isLoading) return <Spinner label="Reading the checklist" />;
  if (checklist.error) {
    /*
      The callable's own words. "Something went wrong" hid a perfectly clear
      "Organization not found" for as long as it took to read the logs, which
      is the whole cost of a generic message on a page only we ever see.
    */
    return (
      <PageState title="Could not read the checklist" tone="alert" action={{ to: '/console/operations/go-live', label: 'Start again' }}>
        {isCallFailure(checklist.error) ? checklist.error.message
          : checklist.error instanceof Error ? checklist.error.message
          : 'Something went wrong.'}
      </PageState>
    );
  }
  const data = checklist.data!;

  return (
    <>
      <OperationsTabs />

      <header className={page.header}>
        <h1 className={page.title}>Go-live</h1>
        <p className={page.subtitle}>
          Every step in the go-live runbook, as state. Half of it is read from the record and
          cannot be ticked; the other half is what a person has to judge, and records who judged
          it. The full prose is in <Link to="/console/operations/runbook">the runbook</Link>.
        </p>
      </header>

      <div className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
        <label className={page.itemName} htmlFor="golive-org">Customer</label>
        <p className={page.itemMeta} style={{ margin: '2px 0 var(--sp-3)' }}>
          The platform section below is the same whichever you choose. The other three are about
          this one customer.
        </p>
        <select
          id="golive-org"
          className={form.select}
          style={{ maxWidth: 420 }}
          value={orgId}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set('org', e.target.value); else next.delete('org');
            setParams(next, { replace: true });
          }}
        >
          <option value="">No customer chosen</option>
          {(orgs.data?.organizations ?? []).map((o) => (
            <option key={o.orgId} value={o.orgId}>{o.name}</option>
          ))}
        </select>
        <p className={page.itemMeta} style={{ marginTop: 'var(--sp-3)' }}>
          <strong>{data.platformDone} of {data.platformTotal}</strong> platform steps
          {orgId ? <> · <strong>{data.customerDone} of {data.customerTotal}</strong> for {data.orgName}</> : null}
        </p>
      </div>

      {setStep.isError && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {setStep.error instanceof Error ? setStep.error.message : 'That could not be saved.'}
        </div>
      )}

      {SECTIONS.map((section) => {
        const steps = bySection.get(section.id) ?? [];
        if (!steps.length) return null;
        const customerSection = section.id !== 'platform';
        const done = steps.filter((s) => s.done).length;
        return (
          <section key={section.id} className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }} aria-labelledby={`sec-${section.id}`}>
            <h2 className={page.sectionTitle} id={`sec-${section.id}`}>
              {section.title}{' '}
              <span className={page.itemMeta} style={{ fontWeight: 400 }}>{done} of {steps.length}</span>
            </h2>
            <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>{section.blurb}</p>

            {customerSection && !orgId ? (
              <p className={page.absent}>Choose a customer above to see where this stands.</p>
            ) : (
              <ul className={page.list} aria-label={section.title}>
                {steps.map((step) => (
                  <li key={step.key} className={page.listItem}>
                    <div style={{ minWidth: 0 }}>
                      {step.source === 'tick' ? (
                        <label className={own.checkRow}>
                          <input
                            type="checkbox"
                            checked={step.done}
                            disabled={setStep.isPending}
                            onChange={(e) => setStep.mutate({ key: step.key, done: e.target.checked, platform: !customerSection })}
                          />
                          <span>
                            <span className={page.itemName}>{step.label}</span>
                            <span className={page.itemMeta} style={{ display: 'block' }}>{step.detail}</span>
                          </span>
                        </label>
                      ) : (
                        <div>
                          <div className={page.itemName}>
                            <span aria-hidden="true" style={{ marginRight: 8 }}>{step.done ? '✓' : '○'}</span>
                            <span className="vh">{step.done ? 'Done: ' : 'To do: '}</span>{step.label}
                            <span className={page.itemMeta} style={{ fontWeight: 400 }}> · read from the record</span>
                          </div>
                          <div className={page.itemMeta}>{step.evidence ?? step.detail}</div>
                        </div>
                      )}

                      {step.preflight && (
                        <p className={page.itemMeta} style={{ margin: '2px 0 0 28px' }}>
                          Asserted by <code>cd tests &amp;&amp; npm run preflight</code>, which checks this
                          whole section at once. Tick it once the suite is green.
                        </p>
                      )}

                      {step.command && (
                        <pre className={own.command}><code>{step.command}</code></pre>
                      )}

                      {step.source === 'tick' && step.done && (
                        <p className={page.itemMeta} style={{ margin: '2px 0 0 28px' }}>
                          {step.byEmail ? `Checked by ${step.byEmail}` : 'Checked'}
                          {on(step.at) ? ` on ${on(step.at)}` : ''}
                          {step.note ? ` — ${step.note}` : ''}
                        </p>
                      )}

                      {step.source === 'tick' && (
                        noteFor === step.key ? (
                          <div style={{ margin: 'var(--sp-2) 0 0 28px', display: 'flex', gap: 'var(--sp-2)' }}>
                            <input
                              className={form.input}
                              value={noteText}
                              maxLength={500}
                              // No placeholder: it would be an example of a
                              // value. "Add a note" said what this is, and
                              // the label says it again for a screen reader.
                              aria-label={`Note for ${step.label}`}
                              onChange={(e) => setNoteText(e.target.value)}
                            />
                            <button type="button" className={page.btn}
                              onClick={() => setStep.mutate({ key: step.key, done: true, note: noteText, platform: !customerSection })}>
                              Save
                            </button>
                            <button type="button" className={page.btn} onClick={() => { setNoteFor(null); setNoteText(''); }}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button type="button" className={page.linkBtn} style={{ margin: '2px 0 0 28px' }}
                            onClick={() => { setNoteFor(step.key); setNoteText(step.note ?? ''); }}>
                            {step.note ? 'Change the note' : 'Add a note'}
                          </button>
                        )
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </>
  );
}
